import { injectable } from "tsyringe";
import { CargoV2ApiRepository } from "@/app/infrastructures/repositories/api/CargoV2ApiRepository";
import {
  CargoV2ListRequest,
  CargoV2DisputeDownloadRequest,
  CargoV2MinimumWeightSummaryApiRequest,
  CargoV2DownloadListRequest,
  CargoV2DetailActivity,
  CargoV2ListClientParentApiRequest,
  CargoV2CalculateRequest,
  CargoV2SearchFlightRequest,
  AwbValidationApiRequest,
  CreateCargoV3ApiRequest,
  CalculateVolumeApiRequestV2,
  ScanApiRequest,
  RequestRtcList,
  RequestCheckEligibleRtcV2,
  DownloadAWBRequest
} from "@/data/payload/api/CargoV2ApiRequest";
import {
  CargoSummaryInProcessRequest,
  FailedUnreadRequest
} from "@/data/payload/api/CargoApiRequest";
import { EditCargoApiRequest } from "@/data/payload/api/CargoApiRequest";
import {
  CargoV2Data,
  CargoV2Entities,
  RtcListEntities,
  RtcData
} from "@/domain/entities/CargoV2";
import { CargoAddResponse, CargoCommodityList } from "@/domain/entities/Cargo";
import { CargoEditResponse } from "@/domain/entities/Cargo";
import { AddCargoRequestInteface } from "@/data/payload/contracts/CargoRequest";
import {
  CargoV2DisputeSummaryData,
  ListClientParentEntities,
  CargoV2FailedUnread,
  CargoV2MinimumWeightSummaryData,
  CargoV2SuccessSummaryData,
  CargoV2SummaryInProcessData,
  CargoV2SearchFlightResult,
  AwbDetailData
} from "@/domain/entities/CargoV2";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";

@injectable()
export class CargoV2Presenter {
  private repository: CargoV2ApiRepository;

  constructor(repository: CargoV2ApiRepository) {
    this.repository = repository;
  }

  public getCargoV2List(params: CargoV2ListRequest): Promise<CargoV2Entities> {
    return this.repository.getCargoV2List(params);
  }

  public getCargoV2ListInProgress(
    params: CargoV2ListRequest
  ): Promise<CargoV2Entities> {
    return this.repository.getCargoV2ListInProgress(params);
  }

  public getCargoV2ListCancelled(
    params: CargoV2ListRequest
  ): Promise<CargoV2Entities> {
    return this.repository.getCargoV2ListCancelled(params);
  }

  public cancelBooking(id: string | number): Promise<ResponsePayload> {
    return this.repository.cancelBooking(id);
  }

  public deleteBooking(id: string | number): Promise<ResponsePayload> {
    return this.repository.deleteBooking(id);
  }

  public getCargoV2Detail(id: string | number): Promise<CargoV2Data> {
    return this.repository.getCargoV2Detail(id);
  }

  public getCargoV2DetailForPrint(
    id: string | number,
    activity?: CargoV2DetailActivity
  ): Promise<CargoV2Data> {
    return this.repository.getCargoV2DetailForPrint(id, activity);
  }

  public getSuccessSummary(
    params: CargoV2ListRequest
  ): Promise<CargoV2SuccessSummaryData> {
    return this.repository.getSuccessSummary(params);
  }

  public getDisputeSummary(
    params: CargoV2ListRequest
  ): Promise<CargoV2DisputeSummaryData> {
    return this.repository.getDisputeSummary(params);
  }

  public getMinimumVolumeSummary(
    params: CargoV2MinimumWeightSummaryApiRequest
  ): Promise<CargoV2MinimumWeightSummaryData> {
    return this.repository.getMinimumVolumeSummary(params);
  }

  public downloadExcelCargoDisputeSummary(
    payload: CargoV2DisputeDownloadRequest
  ): Promise<void> {
    return this.repository.downloadExcelCargoDisputeSummary(payload);
  }

  public downloadMinimumVolume(
    payload: CargoV2MinimumWeightSummaryApiRequest
  ): Promise<void> {
    return this.repository.downloadMinimumVolume(payload);
  }

  public downloadListCargoSuccess(
    payload: CargoV2DownloadListRequest
  ): Promise<ResponsePayload> {
    return this.repository.downloadListCargoSuccess(payload);
  }

  public downloadAwbReceipt(payload: DownloadAWBRequest): Promise<ResponsePayload> {
    return this.repository.downloadAwbReceipt(payload.id);
  }

  public downloadAwbReceiptSync(payload: DownloadAWBRequest): Promise<void> {
    return this.repository.downloadAwbReceiptSync(payload);
  }

  public downloadListCargoFailed(
    params: CargoV2ListRequest
  ): Promise<ResponsePayload> {
    return this.repository.downloadListCargoFailed(params);
  }

  public getSummaryInProcess(
    params: CargoSummaryInProcessRequest
  ): Promise<CargoV2SummaryInProcessData> {
    return this.repository.getSummaryInProcess(params);
  }

  public getCargoFailedUnread(
    params: FailedUnreadRequest
  ): Promise<CargoV2FailedUnread> {
    return this.repository.getCargoFailedUnread(params);
  }

  public getReadyToCargoList(params: RequestRtcList): Promise<RtcListEntities> {
    return this.repository.getReadyToCargoList(params);
  }

  public checkEligible(payload: RequestCheckEligibleRtcV2) {
    return this.repository.checkEligible(payload);
  }

  public getDetailReadyToCargo(id: number): Promise<RtcData> {
    return this.repository.getDetailReadyToCargo(id);
  }

  public createCargoV3(
    payload: AddCargoRequestInteface
  ): Promise<CargoAddResponse> {
    return this.repository.createCargoV3(payload);
  }

  public createCargo(payload: CreateCargoV3ApiRequest) {
    return this.repository.createCargo(payload);
  }

  public editCargoV3(payload: EditCargoApiRequest): Promise<CargoEditResponse> {
    return this.repository.editCargoV3(payload);
  }

  public getCargoCommodityList(
    page: number,
    limit: number,
    search: string
  ): Promise<CargoCommodityList> {
    return this.repository.getCargoCommodityList(page, limit, search);
  }

  public getLastIdRetryCargo(params: CargoV2ListRequest): Promise<number> {
    return this.repository.getLastIdRetryCargo(params);
  }
  public getLastIdCargo(params: CargoV2ListRequest): Promise<number> {
    return this.repository.getLastIdCargo(params);
  }

  public getDownloadData(id: number, type: string, filename = ""): Promise<string> {
    return this.repository.getDownloadReport(id, type, filename);
  }

  public getRTCListClientParent(
    params: CargoV2ListClientParentApiRequest
  ): Promise<ListClientParentEntities> {
    return this.repository.getRTCListClientParent(params);
  }
  public searchFlight(
    payload: CargoV2SearchFlightRequest
  ): Promise<CargoV2SearchFlightResult> {
    return this.repository.searchFlight(payload);
  }

  public calculate(payload: CargoV2CalculateRequest) {
    return this.repository.calculate(payload);
  }

  public getCargoProductList(status: string) {
    return this.repository.getCargoProductList(status);
  }

  public getCargoHistory(id: string) {
    return this.repository.getCargoHistory(id);
  }

  public getAwbValidation(
    payload: AwbValidationApiRequest
  ): Promise<AwbDetailData> {
    return this.repository.getAwbValidation(payload);
  }

  public calculateVolume(payload: CalculateVolumeApiRequestV2) {
    return this.repository.calculateVolume(payload);
  }

  public getCargoTransportType(origin: string, destination: string) {
    return this.repository.getCargoTransportType(origin, destination);
  }

  public getDetailRetryCargo(rcId: string): Promise<CargoV2Data> {
    return this.repository.getDetailRetryCargo(rcId);
  }

  public scan(payload: ScanApiRequest) {
    return this.repository.scan(payload);
  }
}
